import { Component, useEffect, useState } from "react";
import config from "environment";
import "./WazToolbar.css";
import { AppWindow, Files, FileSearch, ListChecks, MessageSquare, Paintbrush, Pin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  APP_MENUS,
  KB_MENUS,
  TOOLS,
  TOOL_COLORS,
  selectEditingCollection,
  selectUIStates,
  selectUser,
  setUIStates,
} from "../../../redux/features/appData";
import { useDispatch } from "react-redux";
import { WazAvatarMenu } from "./WazAvatarMenu";
import { WazSlackMessages } from "../demo/WazSlackMessages";
import { useSlackNotifications } from "../demo/useSlackNotifications";
import { useTaskNotifications } from "./useTaskNotifications";
import { Toaster } from "react-hot-toast";
import { apiGetActiveTasksForOwnerInWorkspace } from "../../../services/TaskService";

const enableWaz = config.enableWaz;

type WazToolbarProps = {
  viewingApp?: boolean;
  viewingKB?: boolean;
};

export const WazToolbar = ({
  viewingApp = false,
  viewingKB = false,
}: WazToolbarProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const uiStates = useSelector(selectUIStates);
  const selectedTool = uiStates.selectedTool || TOOLS.CHAT;
  const editingCollection = useSelector(selectEditingCollection);
  const currentCollectionId = editingCollection?.id;
  const storeUser = useSelector(selectUser);

  const [isExpanded, setIsExpanded] = useState<boolean>(viewingApp);
  
  const [avatarAnchorEl, setAvatarAnchorEl] = useState<null | HTMLElement>(
    null
  );

  const [taskCount, setTaskCount] = useState<number>(0);
  const [hasActiveTasks, setHasActiveTasks] = useState<boolean>(false);

  useEffect(() => {
    if (!storeUser?.employeeRecordId || !storeUser?.currentWorkspaceId) {
      setTaskCount(0);
      setHasActiveTasks(false);
      return;
    }

    let isMounted = true;

    (async () => {
      try {
        const tasks = await apiGetActiveTasksForOwnerInWorkspace(
          storeUser.employeeRecordId || "", 
          storeUser.currentWorkspaceId || ""
        );
        
        if (isMounted) {
          const count = tasks ? tasks.length : 0;
          setTaskCount(count);
          setHasActiveTasks(count > 0);
        }
      } catch (error) {
        console.error("[WazToolbar] Error loading active tasks:", error);
        if (isMounted) {
          setTaskCount(0);
          setHasActiveTasks(false);
        }
      }
    })();

    // Refresh tasks every 30 seconds
    const interval = setInterval(async () => {
      if (!storeUser?.employeeRecordId || !storeUser?.currentWorkspaceId) {
        return;
      }

      try {
        const tasks = await apiGetActiveTasksForOwnerInWorkspace(
          storeUser.employeeRecordId || "", 
          storeUser.currentWorkspaceId || ""
        );
        
        if (isMounted) {
          const count = tasks ? tasks.length : 0;
          setTaskCount(count);
          setHasActiveTasks(count > 0);
        }
      } catch (error) {
        console.error("[WazToolbar] Error refreshing active tasks:", error);
      }
    }, 30000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [storeUser?.employeeRecordId, storeUser?.currentWorkspaceId]);
  // Slack notifications hook
  const { notification, hideNotification } = useSlackNotifications();
  
  // Task notifications hook
  // const { taskCount, hasActiveTasks } = useTaskNotifications();

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAvatarAnchorEl(event.currentTarget);
  };

  const handleAvatarMenuClose = () => {
    setAvatarAnchorEl(null);
  };

  // make sure we set the mode
  if (storeUser) {
    document.body.className = storeUser.theme;
  }

  // const [isKBExpanded, setIsKBExpanded] = useState<boolean>(viewingKB);

  // console.log("[WazToolbar] selectedTool: ", selectedTool);
  // console.log("[WazToolbar] currentCollectionId: ", currentCollectionId);
  // console.log("[WazToolbar] editingCollection: ", editingCollection);

  return (
    <>
      <div className="waz-toolbar">
        {/* <div 
                    className={selectedTool === TOOLS.HOME ? "waz-toolbar-tool-selected" : "waz-toolbar-tool"}
                    onClick={() => {
                        dispatch(setUIStates({...uiStates, selectedTool: TOOLS.HOME}));
                        navigate("/waz/home");
                    }}
                >
                    <Home size={16} />
                </div> */}

        <div
          key={TOOLS.CHAT}
          className={
            selectedTool === TOOLS.CHAT
              ? "waz-toolbar-tool-selected"
              : "waz-toolbar-tool"
          }
          style={selectedTool === TOOLS.CHAT ? {backgroundColor: TOOL_COLORS.CHAT} : {}}

          onClick={() => { 
            if (selectedTool !== TOOLS.CHAT) {
              const menuMap = { ...uiStates.appPath };    
              menuMap[uiStates.selectedTool] = window.location.pathname;
              console.log("[WazToolbar] menuMap: ", menuMap);
              dispatch(setUIStates({
                ...uiStates,
                selectedTool: TOOLS.CHAT,
                appPath: menuMap,
              }));
              navigate("/waz/chat/" + currentCollectionId);
            }
          }}>
          {/* <MessageSquare size={16} color={selectedTool === TOOLS.CHAT ? "#FF543D" : "black"}/> */}
          <MessageSquare size={16} color={selectedTool === TOOLS.CHAT ? "white" : "black"}/>

        </div>

        {config.enableWaz && 
          <>
            <div
              key={TOOLS.TASKS}
              // className="waz-toolbar-login"
              className={
                selectedTool === TOOLS.TASKS
                  ? "waz-toolbar-tool-selected"
                  : "waz-toolbar-tool"
              }
              style={selectedTool === TOOLS.TASKS ? {backgroundColor: TOOL_COLORS.TASKS} : {}}
              onClick={() => {
                if (selectedTool !== TOOLS.TASKS) {
                  // dispatch(
                  //   setUIStates({
                  //     ...uiStates,
                  //     selectedTool: TOOLS.TASKS
                          
                  //   })
                  // );
                  const menuMap = { ...uiStates.appPath };    
                  menuMap[uiStates.selectedTool] = window.location.pathname;
                  console.log("[WazToolbar] menuMap: ", menuMap);
                  dispatch(setUIStates({ 
                    ...uiStates,
                    selectedTool: TOOLS.TASKS,
                    appPath: menuMap,
                  }));
                  
                  setIsExpanded(true);
                  navigate("/tasks");
                
                }
              }}
            >
              <div className="waz-toolbar-tool-content">
                <ListChecks size={16} color={selectedTool === TOOLS.TASKS ? "white" : "black"} />
                {hasActiveTasks && (
                  <div className="waz-toolbar-notification-badge">
                    {taskCount > 99 ? '99+' : taskCount}
                  </div>
                )}
              </div>
            </div>

            <div
              key={TOOLS.HRIS}
              // className="waz-toolbar-login"
              className={
                selectedTool === TOOLS.HRIS
                  ? "waz-toolbar-tool-selected"
                  : "waz-toolbar-tool"
              }
              style={selectedTool === TOOLS.HRIS ? {backgroundColor: TOOL_COLORS.HRIS} : {}}
              onClick={() => {
                if (selectedTool !== TOOLS.HRIS) {
                  const lastPath = uiStates.appPath[TOOLS.HRIS];
                  // dispatch(
                  //   setUIStates({
                  //     ...uiStates,
                  //     selectedTool: TOOLS.HRIS
                          
                  //   })
                  // );
                  const menuMap = { ...uiStates.appPath };    
                  menuMap[uiStates.selectedTool] = window.location.pathname;
                  console.log("[WazToolbar] menuMap: ", menuMap);
                  dispatch(setUIStates({ 
                    ...uiStates,
                    selectedTool: TOOLS.HRIS,
                    appSelectedMenu: uiStates.appSelectedMenu || APP_MENUS.EMPLOYEES,
                    appPath: menuMap,
                  }));
                  
                  console.log("[WazToolbar] lastPath: ", lastPath);
                  setIsExpanded(true);
                  navigate(lastPath || "/app");
                }
              }}
            >
              <AppWindow size={16} color={selectedTool === TOOLS.HRIS ? "white" : "black"} />
            </div>
      
            {/* <div 
              key={TOOLS.DESIGN}
                className={selectedTool === TOOLS.DESIGN ? "waz-toolbar-tool-selected" : "waz-toolbar-tool"}
                onClick={() => {
                    dispatch(setUIStates({...uiStates, selectedTool: TOOLS.DESIGN}));
                    setIsExpanded(!isExpanded);
                    navigate("/waz/admin/design");

                }}
            >
                <Paintbrush size={16} />        
            </div> */}
          </>
        }

        {/* <div
          className={
            selectedTool === TOOLS.PIN
              ? "waz-toolbar-tool-selected"
              : "waz-toolbar-tool"
          }
          onClick={() => {
            dispatch(setUIStates({ ...uiStates, selectedTool: TOOLS.PIN }));
            navigate("/waz/pins");
          }}>
          <Pin size={16} />
        </div> */}

        <div
          key={TOOLS.KB}
          className={
            selectedTool === TOOLS.KB
              ? "waz-toolbar-tool-selected"
              : "waz-toolbar-tool"
          }
          style={selectedTool === TOOLS.KB ? {backgroundColor: TOOL_COLORS.KB} : {}}

          onClick={() => {
            if (selectedTool !== TOOLS.KB) {
            //   dispatch(setUIStates({ ...uiStates, selectedTool: TOOLS.CHAT }));
            //   navigate("/waz/chat/" + currentCollectionId);
            // } else {
              // dispatch(setUIStates({ 
              //   ...uiStates, 
              //   selectedTool: TOOLS.KB,
              //   kbSelectedMenu: KB_MENUS.ACTIVITY
              // }));
              const lastPath = uiStates.appPath[TOOLS.KB];

              const menuMap = { ...uiStates.appPath };    
              menuMap[uiStates.selectedTool] = window.location.pathname;
              console.log("[WazToolbar] menuMap: ", menuMap);

              dispatch(setUIStates({ 
                ...uiStates,
                selectedTool: TOOLS.KB,
                kbSelectedMenu: uiStates.kbSelectedMenu || KB_MENUS.ACTIVITY,
                appPath: menuMap,
              }));

              navigate(lastPath || "/waz/kb");
            }
          }}>
          <FileSearch size={16} color={selectedTool === TOOLS.KB ? "white" : "black"} />
        </div>


        <div 
            className={selectedTool === TOOLS.DOCUMENTS ? "waz-toolbar-tool-selected" : "waz-toolbar-tool"}
            style={selectedTool === TOOLS.DOCUMENTS ? {backgroundColor: TOOL_COLORS.DOCUMENTS} : {}}

            onClick={() => {
                if (selectedTool !== TOOLS.DOCUMENTS) {
                // dispatch(setUIStates({...uiStates, selectedTool: TOOLS.DOCUMENTS}));
                  const menuMap = { ...uiStates.appPath };    
                  menuMap[uiStates.selectedTool] = window.location.pathname;
                  console.log("[WazToolbar] menuMap: ", menuMap);
                  dispatch(setUIStates({ 
                    ...uiStates,
                    selectedTool: TOOLS.DOCUMENTS,
                    appPath: menuMap,
                  }));
                  // setIsExpanded(!isExpanded)
                  navigate("/waz/documents");
                }
            }}
          >
              <Files size={16} color={selectedTool === TOOLS.DOCUMENTS ? "white" : "black"}/>
          </div>

        {/* <div 
              className={selectedTool === TOOLS.TICKET ? "waz-toolbar-tool-selected" : "waz-toolbar-tool"}
              onClick={() => dispatch(setUIStates({...uiStates, selectedTool: TOOLS.TICKET}))}
          >
              <Ticket size={16} />
          </div>

          <div 
              className={selectedTool === TOOLS.NOTIFICATIONS ? "waz-toolbar-tool-selected" : "waz-toolbar-tool"}
              onClick={() => dispatch(setUIStates({...uiStates, selectedTool: TOOLS.NOTIFICATIONS}))}
          >
              <Bell size={16} />
          </div> */}

       


        <div className="waz-toolbar-avatar" onClick={handleAvatarClick}>
          <img src={storeUser?.avatarURL || undefined} alt="avatar" />
        </div>

        <WazAvatarMenu
          anchorEl={avatarAnchorEl}
          onClose={handleAvatarMenuClose}
        />
      </div>

      {/* {isExpanded && selectedTool === TOOLS.APP &&
                <AppSidebar />
            } */}

      {/* Slack Notifications */}
      {/* {notification.isVisible && (
        <WazSlackMessages
          demoIndex={notification.demoIndex}
          onDismiss={hideNotification}
        />
      )} */}

      <Toaster />

    </>
  );
};
